import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import BackgroundMedia from "@/components/BackgroundMedia";
import NarrativePause from "@/components/NarrativePause";
import InstagramSection from "@/components/InstagramSection";
import Seo, { composeTitle, truncateForMeta } from "@/components/Seo";
import { Container, SectionLabel, ButtonLux, Reveal } from "@/components/primitives";
import { useSettings } from "@/context/SettingsContext";
import { api, mediaSrc } from "@/lib/api";

const FALLBACK_HERO_IMG =
  "https://images.unsplash.com/photo-1724775255163-b0f5f5241642?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const FALLBACK_MISSING_1 = "https://images.unsplash.com/photo-1772442363851-738a548f6c5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const FALLBACK_MISSING_2 = "https://images.unsplash.com/photo-1772442364639-20fe5e5438a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const FALLBACK_EXPERIENCE_IMG = "https://images.unsplash.com/photo-1772442198907-765685249ea4?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const FALLBACK_OFFERINGS = [
  { eyebrow: "DESIGNED AROUND YÜ", title: "Bespoke Furniture", line: "Pieces designed for one room, one rhythm, one life.", image_url: "https://images.unsplash.com/photo-1618582383736-ed9080511254?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { eyebrow: "BUILT WITH YÜ", title: "Bespoke Design Solutions", line: "Joinery, lighting and detail work shaped through collaboration.", image_url: "https://images.pexels.com/photos/33342712/pexels-photo-33342712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400" },
  { eyebrow: "CURATED FOR YÜ", title: "Curated Brands", line: "A small, considered roster of partners we trust.", image_url: "https://images.pexels.com/photos/16176580/pexels-photo-16176580.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400" },
  { eyebrow: "SPACES COMPLETED FOR YÜ", title: "Styling & Accessories", line: "The last, quiet layer that makes a space feel inhabited.", image_url: "https://images.unsplash.com/photo-1771797573645-7483eab8207f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { eyebrow: "CREATED AROUND YÜ", title: "Curated Art", line: "Original works sourced and commissioned for the room.", image_url: "https://images.pexels.com/photos/7120840/pexels-photo-7120840.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400" },
];

const HERO_NARRATIVE = [
  "Not when the drawing is finished.",
  "Not when the furniture is placed.",
  "But when every element comes together —",
  "and the space finally feels like yours.",
  "The missing layer between drawing and home.",
];

const EXP_POINTS_FALLBACK = [
  { title: "Bespoke Execution", tagline: "Sketch to install.", body: "From the first idea to the finished piece, bespoke execution can happen within the same working environment." },
  { title: "Styling Support", tagline: "Inside your timeline.", body: "Styling is not an afterthought. It can be brought into the project at the right moment, alongside the design and execution process." },
  { title: "Budget Alignment", tagline: "Full transparency.", body: "Decisions can be refined with clear budget visibility, helping the designer and client move forward with confidence." },
  { title: "Final-Layer Completion", tagline: "Quietly, on cue.", body: "The final details come together when the project is ready — completing the space without disrupting the design process." },
];

function cardSlug(c) {
  const raw = (c.link_slug || "").trim().toLowerCase();
  if (raw) return raw;
  return (c.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function Home() {
  const { settings } = useSettings();
  const [projects, setProjects] = useState([]);
  const [journal, setJournal] = useState([]);
  const [homeDoc, setHomeDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, j, hp] = await Promise.all([
          api.listProjects(),
          api.listJournal(),
          api.getPage("home").catch(() => null),
        ]);
        setProjects(p);
        setJournal(j);
        setHomeDoc(hp);
      } catch (err) {
        console.warn("Home content failed to load", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cms = homeDoc?.content || {};
  const hero = cms.hero || {};
  const heroImgUri = hero.image_url ? mediaSrc(hero.image_url) : FALLBACK_HERO_IMG;
  const heroEyebrow = hero.eyebrow || "Onedot6 · DESIGN COMPLETION PLATFORM";
  const heroHeadline = hero.headline || "When Is A Space\nTruly Complete?";
  const heroSub = hero.sub || "Execution · Final Styling · Completion";

  const missing = cms.missing || {};
  const missingEyebrow = missing.eyebrow || "SECTION · 01";
  const missingH2 = missing.h2 || "The missing layer between drawing and home.";
  const missingBody = missing.body || "Great interiors are often built with care, then left a step short of being lived in. The art is unhung. The lighting is generic. The joinery is almost — but not quite — right.";
  const missingImgA = missing.image_a_url ? mediaSrc(missing.image_a_url) : FALLBACK_MISSING_1;
  const missingImgB = missing.image_b_url ? mediaSrc(missing.image_b_url) : FALLBACK_MISSING_2;
  const missingCapA = missing.image_a_caption || "Before · execution complete.";
  const missingCapB = missing.image_b_caption || "After · the space, finished.";
  const missingPullquote = missing.pullquote || "Onedot6 bridges the gap\nbetween execution and completion.";

  const offeringsBlock = cms.offerings || {};
  const offEyebrow = offeringsBlock.eyebrow || "SECTION · 02 · WHAT WE DO";
  const offH2 = offeringsBlock.h2 || "Five disciplines, one rhythm.";
  const offCards = (Array.isArray(offeringsBlock.cards) && offeringsBlock.cards.length > 0 ? offeringsBlock.cards : FALLBACK_OFFERINGS).filter((o) => o.is_active !== false);

  const featuredBlock = cms.featured_projects || {};
  const featuredEyebrow = featuredBlock.eyebrow || "SECTION · 03 · FEATURED PROJECTS";
  const featuredH2 = featuredBlock.h2 || "Recent work, in long form.";
  const featuredItems = Array.isArray(featuredBlock.items) ? featuredBlock.items : [];
  const featuredProjects = featuredItems.length
    ? featuredItems.map((it) => projects.find((p) => p.id === it.project_id)).filter(Boolean)
    : projects.slice(0, 3);

  const expCta = cms.experience_cta || {};
  const expImgUri = expCta.image_url ? mediaSrc(expCta.image_url) : FALLBACK_EXPERIENCE_IMG;
  const expEyebrow = expCta.eyebrow || "SECTION · 04 · THE EXPERIENCE";
  const expHeadline = expCta.headline || "Not a showroom.\nA working hub, built for YÜ.";
  const expBody = expCta.body || "A collaborative environment to discover, refine and finalize decisions alongside our studio — designed around the way designers actually work.";
  const expCtaLabel = expCta.cta_label || "Visit the Experience Center";
  const expCtaHref = expCta.cta_href || "/experience";
  const expFinalStatement = expCta.final_statement || "Designed around\nthe way you work.";
  const expPointsRaw = Array.isArray(expCta.points) ? expCta.points : [];
  const expPoints = (expPointsRaw.length === 4 ? expPointsRaw : EXP_POINTS_FALLBACK).map((p, i) => ({
    title: p?.title || EXP_POINTS_FALLBACK[i].title,
    tagline: p?.tagline || EXP_POINTS_FALLBACK[i].tagline,
    body: p?.body || EXP_POINTS_FALLBACK[i].body,
    image_url: p?.image_url || "",
  }));

  const finalCta = cms.final_cta || {};
  const finalEyebrow = finalCta.eyebrow || "· LET'S BEGIN ·";
  const finalHeadline = finalCta.headline || "Let's complete the\nspace together.";
  const finalPrimaryLbl = finalCta.cta_primary_label || "Consultation";
  const finalPrimaryHref = finalCta.cta_primary_href || "/contact";
  const finalSecondaryLbl = finalCta.cta_secondary_label || "Visit Experience Center";
  const finalSecondaryHref = finalCta.cta_secondary_href || "/experience";

  const ml = (t) => t.split("\n").map((line, i) => <span key={i} className="block">{line}</span>);

  return (
    <div className="min-h-screen bg-lux-bg">
      <Seo
        title={composeTitle(cms.seo?.title || "", settings.seo.default_title, settings.seo.title_suffix)}
        description={truncateForMeta(cms.seo?.description || hero.body || settings.seo.default_description)}
        path="/"
        imageUrl={cms.seo?.og_image_url ? mediaSrc(cms.seo.og_image_url) : (mediaSrc(settings.seo.default_og_image_url) || heroImgUri)}
      />
      <TopBar />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex flex-col justify-end overflow-hidden px-6 md:px-12 pb-14 md:pb-24 pt-48">
        <BackgroundMedia videoUrl={hero.video_url} imageUrl={heroImgUri} testId="home-hero-media" />
        <div className="absolute inset-0 bg-gradient-to-b from-lux-ink/55 via-lux-ink/20 to-lux-ink/80 pointer-events-none" />
        <div className="relative w-full max-w-[1200px] mx-auto">
          <SectionLabel light>{heroEyebrow}</SectionLabel>
          <h1 data-testid="hero-h1" className="font-serif text-white font-light tracking-[-1.5px] mt-3 text-[54px] leading-[58px] md:text-[64px] md:leading-[68px] lg:text-[96px] lg:leading-[100px] 2xl:text-[116px] 2xl:leading-[118px] [text-shadow:0_1px_3px_rgba(0,0,0,0.30)]">
            {ml(heroHeadline)}
          </h1>
          <p className="text-white/80 text-[12px] md:text-[13px] tracking-[0.25em] uppercase mt-7">{heroSub}</p>
        </div>
      </section>

      {/* NARRATIVE PAUSE */}
      <NarrativePause lines={HERO_NARRATIVE} imageUrl={heroImgUri} />

      {/* MISSING LAYER */}
      <Container id="section-01" className="pt-24 md:pt-40 scroll-mt-24">
        <Reveal>
          <SectionLabel>{missingEyebrow}</SectionLabel>
          <h2 className="font-serif text-lux-ink font-light tracking-tight leading-[1.05] mt-2 text-3xl md:text-5xl max-w-[880px]">{missingH2}</h2>
          <p className="text-lux-sec font-light leading-relaxed mt-8 text-base md:text-lg max-w-[720px]">{missingBody}</p>
        </Reveal>
        <div className="mt-10 md:mt-14 grid md:grid-cols-2 gap-7">
          {[[missingImgA, missingCapA], [missingImgB, missingCapB]].map(([img, cap], i) => (
            <Reveal key={cap} delay={i * 120}>
              <img src={img} alt={cap} className="w-full h-80 md:h-[480px] object-cover bg-lux-line" />
              <p className="mt-3 text-[11px] tracking-[0.2em] uppercase text-lux-muted">{cap}</p>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="font-serif italic text-lux-ink font-light mt-12 md:mt-14 text-2xl md:text-[42px] leading-tight max-w-[880px]">{ml(missingPullquote)}</p>
        </Reveal>
      </Container>

      {/* OFFERINGS */}
      <Container className="pt-24 md:pt-40">
        <Reveal>
          <SectionLabel>{offEyebrow}</SectionLabel>
          <h2 className="font-serif text-lux-ink font-light tracking-tight leading-[1.05] mt-2 text-3xl md:text-5xl">{offH2}</h2>
        </Reveal>
        <div className="mt-10 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {offCards.map((o, i) => {
            const slug = cardSlug(o);
            const explicit = typeof o.link_href === "string" ? o.link_href.trim() : "";
            const to = explicit || (slug ? `/bespoke?section=${slug}` : "/bespoke");
            return (
              <Reveal key={`${o.title}-${i}`} delay={(i % 3) * 100}>
                <Link to={to} data-testid={`offering-${i}`} className="group block bg-lux-surface border border-lux-line h-full">
                  <div className="overflow-hidden h-64 md:h-80 bg-lux-line">
                    <img src={o.image_url ? mediaSrc(o.image_url) : ""} alt={o.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6 md:p-7">
                    <p className="text-[11px] tracking-[0.25em] text-lux-accent mb-2.5">{o.eyebrow}</p>
                    <h3 className="font-serif text-2xl text-lux-ink font-light tracking-tight mb-2.5">{o.title}</h3>
                    <p className="text-[15px] text-lux-sec leading-relaxed font-light">{o.line}</p>
                    {o.cta_label ? <p className="mt-4 text-[11px] tracking-[0.2em] text-lux-accent">{String(o.cta_label).toUpperCase()}</p> : null}
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </Container>

      {/* FEATURED PROJECTS */}
      <Container className="pt-24 md:pt-40">
        <Reveal>
          <SectionLabel>{featuredEyebrow}</SectionLabel>
          <h2 className="font-serif text-lux-ink font-light tracking-tight leading-[1.05] mt-2 text-3xl md:text-5xl">{featuredH2}</h2>
        </Reveal>
        <div className="mt-10 md:mt-14">
          {loading ? (
            <p className="text-lux-muted italic">Loading…</p>
          ) : featuredProjects.length === 0 ? (
            <p className="text-lux-muted italic">No featured projects yet.</p>
          ) : (
            featuredProjects.map((p, idx) => (
              <Reveal key={p.id}>
                <Link
                  to={`/projects/${p.id}`}
                  data-testid={`featured-project-${p.id}`}
                  className={`group flex flex-col md:flex-row gap-6 md:gap-12 items-center mb-10 md:mb-24 ${idx % 2 === 1 ? "md:flex-row-reverse" : ""}`}
                >
                  <div className="w-full md:flex-[1.4] overflow-hidden bg-lux-line">
                    <img src={mediaSrc(p.image_url)} alt={p.name} className="w-full h-80 md:h-[540px] object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  </div>
                  <div className="flex-1 md:pt-0 pt-2">
                    <p className="text-[11px] tracking-[0.25em] text-lux-accent mb-2.5">WITH · {p.designer.toUpperCase()}</p>
                    <h3 className="font-serif text-lux-ink font-light tracking-tight leading-tight text-3xl md:text-[40px]">{p.name}</h3>
                    <p className="text-lux-sec font-light leading-relaxed mt-4 text-base md:text-[17px] max-w-[520px]">{p.summary}</p>
                    {p.location ? <p className="text-[11px] tracking-[0.2em] uppercase text-lux-muted mt-4">{p.location}</p> : null}
                  </div>
                </Link>
              </Reveal>
            ))
          )}
          <ButtonLux label="View All Projects" variant="outline" to="/projects" testId="view-all-projects" />
        </div>
      </Container>

      {/* THE EXPERIENCE */}
      <section className="mt-24 md:mt-40">
        <div className="relative min-h-[480px] md:min-h-[620px] flex items-end overflow-hidden">
          <BackgroundMedia videoUrl={expCta.video_url} imageUrl={expImgUri} testId="home-exp-media" />
          <div className="absolute inset-0 bg-gradient-to-b from-lux-ink/35 to-lux-ink/85 pointer-events-none" />
          <Container className="relative pb-12 md:pb-24 pt-16 md:pt-24">
            <div className="max-w-[780px]">
              <SectionLabel light>{expEyebrow}</SectionLabel>
              <h2 className="font-serif text-white font-light tracking-tight leading-tight mt-2 text-[clamp(2.4rem,5vw,4rem)]">{ml(expHeadline)}</h2>
              <p className="text-white/80 font-light leading-relaxed mt-5 text-base md:text-lg">{expBody}</p>
            </div>
          </Container>
        </div>
        <Container className="pt-12 md:pt-20 pb-4">
          {expPoints.map((pt, i) => (
            <Reveal key={pt.title || i}>
              <div className={`flex flex-col md:flex-row gap-6 md:gap-14 items-center py-8 md:py-12 border-t border-lux-line ${i % 2 === 1 ? "md:flex-row-reverse" : ""}`}>
                <div className="flex-1 w-full overflow-hidden bg-lux-line">
                  <img src={pt.image_url ? mediaSrc(pt.image_url) : expImgUri} alt={pt.title} className="w-full h-56 md:h-72 object-cover" />
                </div>
                <div className="flex-1">
                  <p className="text-[11px] tracking-[0.25em] text-lux-accent mb-2">0{i + 1} · {pt.tagline.toUpperCase()}</p>
                  <h3 className="font-serif text-2xl md:text-3xl text-lux-ink font-light tracking-tight">{pt.title}</h3>
                  <p className="text-lux-sec font-light leading-relaxed mt-3 text-[15px] md:text-base max-w-[480px]">{pt.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal>
            <div className="border-t border-lux-line pt-10 md:pt-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
              <p className="font-serif italic text-lux-ink font-light text-2xl md:text-4xl leading-tight">{ml(expFinalStatement)}</p>
              <ButtonLux label={expCtaLabel} to={expCtaHref} testId="exp-cta" />
            </div>
          </Reveal>
        </Container>
      </section>

      {/* JOURNAL */}
      <Container className="pt-24 md:pt-40">
        <Reveal>
          <SectionLabel>SECTION · 05 · JOURNAL</SectionLabel>
          <h2 className="font-serif text-lux-ink font-light tracking-tight leading-[1.05] mt-2 text-3xl md:text-5xl">Notes on designing<br />around YÜ.</h2>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-3 gap-8">
          {journal.slice(0, 3).map((j) => (
            <Reveal key={j.id}>
              <Link to={`/journal/${j.id}`} data-testid={`journal-card-${j.id}`} className="group block">
                <div className="overflow-hidden bg-lux-line h-72 md:h-[360px] mb-4">
                  <img src={mediaSrc(j.image_url)} alt={j.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <p className="text-[10px] tracking-[0.25em] text-lux-accent mb-2">{(j.category || "").toUpperCase()}</p>
                <h3 className="font-serif text-2xl text-lux-ink font-light leading-snug mb-2">{j.title}</h3>
                <p className="text-[15px] text-lux-sec leading-relaxed font-light line-clamp-3">{j.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-8">
          <ButtonLux label="Read the Journal" variant="outline" to="/journal" testId="read-journal-btn" />
        </div>
      </Container>

      <InstagramSection />

      {/* FINAL CTA */}
      <section className="mt-24 md:mt-40 bg-lux-ink px-6 md:px-12 py-20 md:py-40">
        <div className="w-full max-w-[1200px] mx-auto flex flex-col md:items-center">
          <SectionLabel light>{finalEyebrow}</SectionLabel>
          <h2 className="font-serif text-white font-light tracking-tight leading-tight mt-3 md:text-center text-[clamp(2.5rem,6vw,4.5rem)]">{ml(finalHeadline)}</h2>
          <div className="mt-8 flex flex-col md:flex-row gap-4">
            <ButtonLux label={finalPrimaryLbl} to={finalPrimaryHref} light testId="final-consultation-btn" />
            <ButtonLux label={finalSecondaryLbl} variant="outline" to={finalSecondaryHref} light testId="final-experience-btn" />
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFAB />
    </div>
  );
}
